import { useState } from "react";
import { Button, Dialog } from "./components.js";
import { TextAreaField } from "./controls.js";
import { notify } from "./toast.js";

/**
 * What a report carries besides the text: the page path and the viewport size. The query string is left out on
 * purpose: links can carry tokens (password reset, staff hand-off) that must never end up in a report.
 */
export function pageContext(loc: Pick<Location, "pathname"> = window.location, w = window.innerWidth, h = window.innerHeight) {
  return { page: loc.pathname.slice(0, 500), viewport: `${Math.round(w)}x${Math.round(h)}` };
}

/**
 * "Report a problem" (A.41), shared by the portal and the console. The app supplies `submit`, which posts to
 * /v1/me/bug-reports with its own API client. The dialog says exactly what is sent with the report.
 */
export function ReportProblemDialog({ open, onClose, submit }: { open: boolean; onClose: () => void; submit: (description: string) => Promise<unknown> }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = text.trim().length >= 5;
  const send = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await submit(text.trim());
      notify.success("Thanks, we got it", "The FlexFreight team reviews every report.");
      setText("");
      onClose();
    } catch (e) {
      notify.error("Couldn't send the report", e instanceof Error ? e.message : "Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Report a problem"
      description="Tell us what happened and what you expected. We include the page you're on and your browser and screen size, nothing else."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={busy} onClick={() => void send()}>Send report</Button>
        </>
      }
    >
      <TextAreaField label="What went wrong?" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={4000} />
    </Dialog>
  );
}
